/* DataTableComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
//const _ = require("lodash");

const eventRegisterMixin = require("../../utils/eventRegisterMixin");
const Renderer = require("./DataTableRenderer");

/**
 * @class DataTableComponent
 *
 * @fires DataTableComponent#Title
 * @fires DataTableComponent#Data
 * @fires DataTableComponent#UpdateItem
 * @fires DataTableComponent#AddItem
 * @fires DataTableComponent#RemoveItem
 * @fires DataTableComponent#Controls
 * @fires DataTableComponent#Loading
 * @fires DataTableComponent#Selected
 * @fires DataTableComponent#Shutdown
 */
class DataTableComponent extends eventRegisterMixin(EventEmitter) {
    constructor({ title, buttons, isSelectable, noDataMessage, actions, gridFiltering }) {
        super();

        this._registerListeners();

        this.actions = actions || {};
        this._columns = this._getColumns();

        this._feed = undefined;
        this._data = [];
        this._selected = [];

        this._renderer = new Renderer(this, isSelectable, noDataMessage, gridFiltering);

        this.setTableTitle(title);
        this.setTableControls(buttons);
        this.setLoading(true);
    }

    get renderer() {
        return this.getRenderer();
    }

    get rootElement() {
        return this._renderer && this._renderer.getRootElement();
    }

    get columns() {
        return this._columns;
    }

    set columns(columns) {
        this._columns = columns;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/

    _registerListeners() {}
    _registerFeedListeners(feed) {
        this._registerListener(feed, "StatusChanged", (status) => {
            switch (status) {
                case "ESTABLISHING":
                case "RE_INITIALIZING":
                    this.setLoading(true);
                    break;
                case "INITIALIZED":
                case "RE_INITIALIZED":
                    this.setLoading(false);
                    break;
            }
        });

        this._registerListener(feed, "ShutdownStarted", () => this._unregisterAllListeners(feed), "once");

        this._registerListener(feed, "ChangeFeedInitialized", (data, feed /*, reInit*/) => {
            this.setData(feed.getCacheAsArray());
        });
        this._registerListener(feed, "EntityAdded", (entity, properties, feed) => {
            this.setData(feed.getCacheAsArray(), true);
            this._triggerAddItem(entity);
        });
        this._registerListener(feed, "EntityRemoved", (entity, cause, feed) => {
            this.setData(feed.getCacheAsArray(), true);
            this._triggerRemoveItem(entity);
        });
        this._registerListener(feed, "EntityPropertiesUpdated", (entity, patches, props, feed) => {
            this.setData(feed.getCacheAsArray(), true);
            this._triggerUpdateItem(entity);
        });
    }

    _unregisterListeners() {
        this._unregisterAllListeners();
    }

    _shutdown() {
        this.emit("Shutdown");
        this._unregisterListeners();

        return Promise.all([this._renderer.shutdown()])
            .then(() => {
                this._renderer = null;
                this._shutdownProm = null;
                this._listeners = null;
                this._data = null;
                this._selected = null;
                this.removeAllListeners();
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    _triggerTitle(title) {
        /**
         * @event DataTableComponent#Title
         * @param {string} title - new title for the table
         */
        this.emit("Title", title);
    }

    _triggerData(data) {
        /**
         * @event DataTableComponent#Data
         * @param {Object[]} data - new data for the table
         */
        this.emit("Data", data);
    }
    _triggerUpdateItem(item) {
        /**
         * @event DataTableComponent#UpdateItem
         * @param {Object} item - new data for the table
         */
        this.emit("UpdateItem", item);
    }
    _triggerRemoveItem(item) {
        /**
         * @event DataTableComponent#RemoveItem
         * @param {Object} item - new data for the table
         */
        this.emit("RemoveItem", item);
    }
    _triggerAddItem(item) {
        /**
         * @event DataTableComponent#AddItem
         * @param {Object} item - new data for the table
         */
        this.emit("AddItem", item);
    }

    _triggerControls(controls) {
        /**
         * @event DataTableComponent#Controls
         * @param {Object[]} controls - new controls for the table
         */
        this.emit("Controls", controls);
    }

    _triggerLoading(isLoading) {
        /**
         * @event DataTableComponent#Loading
         * @param {boolean} isLoading - emits loading state
         */
        this.emit("Loading", isLoading);
    }

    _triggerSelected(row, selected) {
        /**
         * @event DataTableComponent#Selected
         * @param {{row: number[], ids: string[]}} selected - selected object
         */
        this.emit("Selected", { rows: [].concat(row), ids: selected });
    }
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    _getColumns() {
        return [];
    }

    _parseData(data) {
        return data;
    }

    getItemMetadata(metaData) {
        return metaData;
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    setFeed(feed) {
        if (feed && feed !== this._feed) {
            this._unregisterAllListeners(this._feed);
            this.setLoading(true);
            this._registerFeedListeners(feed);
            this._feed = feed;
        }
        return this;
    }

    setTableTitle(title) {
        if (title !== undefined) {
            this._triggerTitle(title);
        }
        return this;
    }

    setData(data, silent) {
        if (data !== undefined) {
            this._data = this._parseData(data);
            if (!silent) {
                this._triggerData(this._data);
            }
        }
        this._triggerLoading(false);
        return this;
    }

    getData() {
        return this._data;
    }

    setLoading(state = true) {
        state = Boolean(state);
        if (this._isLoading !== state) {
            this._isLoading = state;
            this._triggerLoading(state);
        }
        return this;
    }

    setTableControls(controls) {
        if (controls !== undefined) {
            this._triggerControls(controls);
        }
        return this;
    }

    setSelected(row, item) {
        const id = item && item.id;
        const currentSelected = this.getSelected();
        //check if license is inherited inherited here;

        if (currentSelected.includes(id) || !id) {
            this._selected = [];
            row = [];
        } else if (id) {
            this._selected = [id];
        }
        this._triggerSelected(row, this._selected);
        return this;
    }

    getSelected() {
        return this._selected;
    }

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

module.exports = DataTableComponent;
